import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminLayout from '@/components/layout/AdminLayout';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Brain, Layers, Inbox, Sparkles, Target, Clock, RefreshCw, ShieldCheck,
  CheckCircle2, XCircle, User, Activity, FileText, AlertTriangle, Dna,
  Stethoscope, FlaskConical, Zap, HeartPulse, LifeBuoy, ChevronRight,
  ChevronDown, Eye, EyeOff, CheckCircle, AlertCircle, Hourglass, Loader2,
  UserRound,
} from 'lucide-react';
import { MASTER_INTAKE_SCHEMA, COUNSELOR_FOLLOWUP_SCHEMA } from '@/schemas/intake_schemas';
import AdminPerceptionStatusPill from '@/components/AdminPerceptionStatusPill';
import PolishedNarrativeCard from '@/components/PolishedNarrativeCard';

// ── Confidence pill colors ──
const CONF_COLOR = {
  low: '#64748b',
  medium: '#D4A843',
  high: '#10b981',
};
const FRESHNESS_COLOR = {
  new: '#64748b',
  current: '#10b981',
  partly_stale: '#D4A843',
  stale: '#f43f5e',
  needs_review: '#f43f5e',
};
const SOURCE_ICON = {
  lab: FlaskConical,
  execution: Zap,
  twinflo: Target,
  passive: Activity,
  support: LifeBuoy,
  report: FileText,
  unknown: Brain,
};

// ═════════════════════════════════════════════════════════════════════
//  MAIN PAGE
// ═════════════════════════════════════════════════════════════════════
export default function BiologicalContextMemory() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [subscribers, setSubscribers] = useState([]);
  const [selectedSubId, setSelectedSubId] = useState(searchParams.get('subscriber') || '');
  const [schema, setSchema] = useState(null);
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(searchParams.get('tab') || 'canonical');

  // ── Load subscribers + schema ──
  useEffect(() => {
    axios.get(`${API}/biocontext-memory/schema`).then(r => setSchema(r.data)).catch(() => {});
    axios.get(`${API}/users?role=subscriber`).then(r => {
      const list = Array.isArray(r.data) ? r.data : (r.data?.items || r.data?.users || []);
      setSubscribers(list);
      if (!selectedSubId && list.length > 0) {
        setSelectedSubId(list[0].id);
      }
    }).catch(() => {});
  }, []); // eslint-disable-line

  // ── Keep URL in sync with selection ──
  useEffect(() => {
    if (selectedSubId) {
      const p = new URLSearchParams(searchParams);
      p.set('subscriber', selectedSubId);
      p.set('tab', tab);
      setSearchParams(p, { replace: true });
    }
  }, [selectedSubId, tab]); // eslint-disable-line

  // ── Load memory doc for selected subscriber ──
  const loadMemory = useCallback(async () => {
    if (!selectedSubId) return;
    setLoading(true);
    try {
      const [mem, hist] = await Promise.all([
        axios.get(`${API}/biocontext-memory/${selectedSubId}`),
        axios.get(`${API}/biocontext-memory/${selectedSubId}/history`),
      ]);
      setData(mem.data);
      setHistory(hist.data?.history || []);
    } catch (e) {
      console.error(e);
      if (e.response?.status === 403) toast.error('Full Biological Context is admin-only.');
    } finally {
      setLoading(false);
    }
  }, [selectedSubId]);

  useEffect(() => { loadMemory(); }, [loadMemory]);

  const selectedSub = useMemo(
    () => subscribers.find(s => s.id === selectedSubId),
    [subscribers, selectedSubId]
  );

  const isAdmin = user?.role === 'admin';

  return (
    <AdminLayout>
      <div className="space-y-5" data-testid="biocontext-memory-page">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-[10.5px] uppercase tracking-[0.28em] text-white/30 mb-1.5 font-['Work_Sans']">
              ReGenflō Care OS · Living Memory Engine
            </p>
            <h1 className="text-2xl sm:text-3xl font-['Outfit'] font-light text-white/90" data-testid="biocontext-memory-title">
              Biological Context Memory — Canonical
            </h1>
            <p className="text-xs text-white/40 font-['Work_Sans'] mt-1 max-w-2xl">
              Admin-owned canonical memory. Fed by subscriber intake, counselor input,
              and system evidence. Consumed by the ReGenflō Intelligence Backbone.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="border-white/10 text-white/60 text-[10px]" data-testid="role-badge">
              <ShieldCheck className="w-3 h-3 mr-1" /> {user?.role || 'guest'} · {isAdmin ? 'full' : 'limited'}
            </Badge>
            <Button variant="ghost" size="sm" className="text-white/30 hover:text-white/60"
                    onClick={loadMemory} data-testid="refresh-btn">
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              <span className="text-[10px]">Refresh</span>
            </Button>
          </div>
        </div>

        {/* Layout: sidebar + workspace */}
        <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-5">
          {/* Subscriber picker */}
          <SubscriberSidebar
            subscribers={subscribers}
            selectedSubId={selectedSubId}
            onSelect={setSelectedSubId}
          />

          {/* Workspace */}
          {!selectedSubId ? (
            <div className="rounded-xl p-10 border border-white/[0.06] text-center text-white/40 text-xs">
              Select a subscriber from the left to begin.
            </div>
          ) : !data ? (
            <div className="rounded-xl p-10 border border-white/[0.06] text-center text-white/40 text-xs">
              <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2 text-white/30" /> Loading memory…
            </div>
          ) : (
            <div className="space-y-4" data-testid="memory-workspace">
              {/* Summary header */}
              <MemoryHeader data={data} selectedSub={selectedSub} />

              {/* LLM-polished narrative — admin/care-team panel */}
              <PolishedNarrativeCard subscriberId={selectedSubId} variant="admin" />

              {/* Tabs */}
              <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="bg-white/[0.02] border border-white/[0.06] h-auto p-1">
                  <TabsTrigger value="canonical" className="text-[11px]" data-testid="tab-canonical">
                    <Layers className="w-3 h-3 mr-1" /> Canonical
                  </TabsTrigger>
                  <TabsTrigger value="input-queue" className="text-[11px]" data-testid="tab-input-queue">
                    <Inbox className="w-3 h-3 mr-1" /> Input Queue
                    {(
                      (data.input_queue?.subscriber_intakes?.length || 0)
                      + (data.input_queue?.counselor_followups?.length || 0)
                      + (data.input_queue?.counselor_observations?.length || 0)
                    ) > 0 && (
                      <span className="ml-1 px-1.5 rounded-full bg-amber-500/20 text-amber-400 text-[9px]">
                        {(data.input_queue?.subscriber_intakes?.length || 0)
                         + (data.input_queue?.counselor_followups?.length || 0)
                         + (data.input_queue?.counselor_observations?.length || 0)}
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="suggestions" className="text-[11px]" data-testid="tab-suggestions">
                    <Sparkles className="w-3 h-3 mr-1" /> Suggestions
                    {(() => {
                      const sugg = (data.system_suggestions || []).filter(s => s.status === 'suggested').length;
                      return sugg > 0 && <span className="ml-1 px-1.5 rounded-full bg-[#7E9EC8]/20 text-[#7E9EC8] text-[9px]">{sugg}</span>;
                    })()}
                  </TabsTrigger>
                  <TabsTrigger value="active-case" className="text-[11px]" data-testid="tab-active-case">
                    <Target className="w-3 h-3 mr-1" /> Active Case
                  </TabsTrigger>
                  <TabsTrigger value="history" className="text-[11px]" data-testid="tab-history">
                    <Clock className="w-3 h-3 mr-1" /> History
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="canonical" className="mt-4">
                  <CanonicalTab data={data} schema={schema} onUpdated={loadMemory} />
                </TabsContent>
                <TabsContent value="input-queue" className="mt-4">
                  <InputQueueTab data={data} subscriberId={selectedSubId} onReload={loadMemory} />
                </TabsContent>
                <TabsContent value="suggestions" className="mt-4">
                  <SuggestionsTab data={data} onReview={loadMemory} />
                </TabsContent>
                <TabsContent value="active-case" className="mt-4">
                  <ActiveCaseTab data={data} onUpdated={loadMemory} />
                </TabsContent>
                <TabsContent value="history" className="mt-4">
                  <HistoryTab history={history} />
                </TabsContent>
              </Tabs>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

// ═════════════════════════════════════════════════════════════════════
//  Sidebar — subscriber list
// ═════════════════════════════════════════════════════════════════════
function SubscriberSidebar({ subscribers, selectedSubId, onSelect }) {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    return subscribers.filter(s =>
      (s.name || '').toLowerCase().includes(term) || (s.email || '').toLowerCase().includes(term)
    );
  }, [subscribers, search]);

  return (
    <div className="rounded-xl border border-white/[0.06] bg-[#161B22] p-3 h-fit sticky top-4" data-testid="subscriber-sidebar">
      <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2 font-['Work_Sans'] px-1">
        Subscribers ({filtered.length})
      </p>
      <Input
        placeholder="Search…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="bg-white/[0.02] border-white/[0.08] text-white/80 h-8 text-xs mb-2"
        data-testid="subscriber-search"
      />
      <div className="max-h-[70vh] overflow-y-auto space-y-0.5 pr-1">
        {filtered.length === 0 ? (
          <p className="text-[11px] text-white/25 italic px-2 py-4">No subscribers found.</p>
        ) : filtered.map(s => (
          <button
            key={s.id}
            onClick={() => onSelect(s.id)}
            data-testid={`sub-item-${s.id}`}
            className={`w-full text-left p-2 rounded-lg transition-colors ${
              selectedSubId === s.id
                ? 'bg-white/[0.08] text-white/90'
                : 'text-white/55 hover:bg-white/[0.03] hover:text-white/80'
            }`}
          >
            <p className="text-xs font-['Work_Sans'] truncate">{s.name || 'Unnamed'}</p>
            <p className="text-[10px] text-white/30 truncate mt-0.5">{s.email}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
//  Memory Header — summary of current state
// ═════════════════════════════════════════════════════════════════════
function MemoryHeader({ data, selectedSub }) {
  const meta = data.meta || {};
  const sub = data.subscriber || selectedSub || {};
  const freshness = meta.context_freshness || 'new';
  return (
    <div className="rounded-xl p-4 border border-white/[0.06] bg-[#161B22]" data-testid="memory-header">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <User className="w-3.5 h-3.5 text-white/40" />
            <h2 className="text-base font-['Outfit'] font-light text-white/90 truncate">
              {sub.name || 'Subscriber'}
            </h2>
            {(sub.conditions || []).map(c => (
              <Badge key={c} variant="outline" className="border-[#a78bfa]/30 text-[#a78bfa] text-[9px]">
                {c}
              </Badge>
            ))}
          </div>
          <p className="text-[10px] text-white/30 mt-0.5 font-['Work_Sans']">{sub.email}</p>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <Stat label="Completeness" value={`${meta.completeness_score || 0}%`} color="#10b981" />
          <Stat label="Freshness" value={freshness.replace('_', ' ')} color={FRESHNESS_COLOR[freshness]} />
          <Stat label="Status" value={data.status || 'draft'} color="#7E9EC8" />
          <Stat label="Overlays" value={(data.condition_overlay_mode || []).join(', ') || '—'} color="#a78bfa" />
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2 text-[10px] text-white/40 font-['Work_Sans']">
        <SourceChip label="Subscriber" active={meta.source_mix?.subscriber} />
        <SourceChip label="Counselor" active={meta.source_mix?.counselor} />
        <SourceChip label="Admin" active={meta.source_mix?.admin} />
        <SourceChip label="System" active={meta.source_mix?.system} />
        <AdminPerceptionStatusPill subscriberId={sub.id || data.subscriber_id} compact />
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div>
      <p className="text-[9.5px] uppercase tracking-[0.15em] text-white/30 font-['Work_Sans']">{label}</p>
      <p className="text-sm font-['Outfit'] font-light mt-0.5" style={{ color }}>{value}</p>
    </div>
  );
}

function SourceChip({ label, active }) {
  return (
    <span className={`px-2 py-0.5 rounded-full border text-[10px] ${active
      ? 'border-emerald-500/30 bg-emerald-500/5 text-emerald-400'
      : 'border-white/10 text-white/30'}`}>
      {active ? '✓' : '○'} {label}
    </span>
  );
}

// ═════════════════════════════════════════════════════════════════════
//  Tab 1 — Canonical Context
// ═════════════════════════════════════════════════════════════════════
function CanonicalTab({ data, schema, onUpdated }) {
  if (!schema) {
    return <p className="text-xs text-white/30 italic">Loading schema…</p>;
  }
  const canonical = data.canonical_context || {};
  const universal = schema.universal_sections || [];
  const extended = schema.extended_profiles || [];
  const overlays = data.condition_overlay_mode || [];
  const overlaySchemas = overlays.map(k => ({ key: `overlay_${k}`, label: schema.condition_overlays?.[k]?.label || k, signals: schema.condition_overlays?.[k]?.signals || [] }));

  return (
    <div className="space-y-4" data-testid="canonical-tab">
      <Section title="Universal Sections">
        <div className="space-y-2">
          {universal.map(s => (
            <CanonicalSection
              key={s.key} schema={s} node={canonical[s.key] || {}}
              subscriberId={data.subscriber_id} onUpdated={onUpdated}
            />
          ))}
        </div>
      </Section>

      <Section title="Extended Profiles">
        <div className="space-y-2">
          {extended.map(s => (
            <CanonicalSection
              key={s.key} schema={s} node={canonical[s.key] || {}}
              subscriberId={data.subscriber_id} onUpdated={onUpdated}
            />
          ))}
        </div>
      </Section>

      {overlaySchemas.length > 0 && (
        <Section title="Condition Overlays">
          <div className="space-y-2">
            {overlaySchemas.map(s => (
              <CanonicalSection
                key={s.key} schema={s} node={canonical[s.key] || {}}
                subscriberId={data.subscriber_id} onUpdated={onUpdated}
              />
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.2em] text-white/35 mb-2 font-['Work_Sans']">{title}</p>
      {children}
    </div>
  );
}

function CanonicalSection({ schema, node, subscriberId, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState(node.canonical_summary || '');
  const [confidence, setConfidence] = useState(node.confidence_level || 'low');
  const [signals, setSignals] = useState(node.structured_signals || {});
  const [saving, setSaving] = useState(false);

  const signalKeys = schema.signals || [];
  const filledSignals = Object.entries(signals).filter(([, v]) => v !== '' && v !== null && v !== undefined);
  const hasSummary = (node.canonical_summary || '').trim().length > 0;

  useEffect(() => {
    setSummary(node.canonical_summary || '');
    setConfidence(node.confidence_level || 'low');
    setSignals(node.structured_signals || {});
  }, [node]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/biocontext-memory/${subscriberId}/canonical/${schema.key}`,
        {
          canonical_summary: summary,
          structured_signals: signals,
          confidence_level: confidence,
        },
      );
      toast.success(`${schema.label} saved`);
      onUpdated?.();
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.015]" data-testid={`section-${schema.key}`}>
      <button
        className="w-full flex items-center gap-3 p-3 text-left hover:bg-white/[0.02] transition-colors"
        onClick={() => setOpen(!open)}
      >
        <ChevronRight className={`w-3.5 h-3.5 text-white/40 transition-transform ${open ? 'rotate-90' : ''}`} />
        <div className="flex-1 min-w-0">
          <p className="text-xs text-white/80 font-['Work_Sans']">{schema.label}</p>
          {hasSummary && (
            <p className="text-[10.5px] text-white/45 mt-0.5 line-clamp-1 font-['Work_Sans']">
              {node.canonical_summary}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Badge variant="outline" className="border-white/10 text-[9px]" style={{ color: CONF_COLOR[node.confidence_level || 'low'] }}>
            {node.confidence_level || 'low'}
          </Badge>
          {filledSignals.length > 0 && (
            <span className="text-[9.5px] text-white/40 font-mono">{filledSignals.length}/{signalKeys.length}</span>
          )}
        </div>
      </button>

      {open && (
        <div className="border-t border-white/[0.05] p-4 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-white/40 mb-1 block font-['Work_Sans']">
              Canonical summary
            </label>
            <Textarea
              value={summary}
              onChange={e => setSummary(e.target.value)}
              placeholder="Admin-approved summary that downstream intelligence can read…"
              rows={3}
              className="bg-white/[0.02] border-white/[0.08] text-white/80 text-xs font-['Work_Sans']"
              data-testid={`summary-${schema.key}`}
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-white/40 mb-1 block font-['Work_Sans']">
              Structured signals
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {signalKeys.map(k => (
                <div key={k}>
                  <p className="text-[10px] text-white/40 mb-0.5 font-['Work_Sans']">{k.replace(/_/g, ' ')}</p>
                  <Input
                    value={signals[k] || ''}
                    onChange={e => setSignals({ ...signals, [k]: e.target.value })}
                    placeholder="—"
                    className="bg-white/[0.02] border-white/[0.08] text-white/80 h-7 text-xs"
                    data-testid={`signal-${schema.key}-${k}`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-white/40">Confidence:</span>
              {['low', 'medium', 'high'].map(lvl => (
                <button
                  key={lvl}
                  onClick={() => setConfidence(lvl)}
                  data-testid={`confidence-${schema.key}-${lvl}`}
                  className={`px-2 py-0.5 rounded-full text-[10px] border transition-colors ${
                    confidence === lvl
                      ? 'text-white'
                      : 'text-white/40 border-white/10 hover:text-white/60'
                  }`}
                  style={confidence === lvl
                    ? { backgroundColor: `${CONF_COLOR[lvl]}25`, borderColor: `${CONF_COLOR[lvl]}60`, color: CONF_COLOR[lvl] }
                    : {}}
                >
                  {lvl}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" className="h-7 text-[10px] text-white/40" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button size="sm" className="h-7 text-[10px] bg-[#D4A843] text-black hover:bg-[#D4A843]/80"
                      onClick={save} disabled={saving} data-testid={`save-${schema.key}`}>
                {saving ? 'Saving…' : 'Save section'}
              </Button>
            </div>
          </div>

          {(node.last_updated_at || node.source_trace) && (
            <div className="pt-2 border-t border-white/[0.05] flex items-center gap-3 text-[9.5px] text-white/30 font-['Work_Sans']">
              {node.last_updated_at && <span>Last edited: {new Date(node.last_updated_at).toLocaleString()}</span>}
              {node.source_trace && (
                <span>
                  Sources: {Object.entries(node.source_trace).filter(([, v]) => v).map(([k]) => k).join(', ') || 'none'}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
//  Tab 2 — Input Queue (rich, expandable, reviewable)
// ═════════════════════════════════════════════════════════════════════

// Build quick lookup maps from the existing schemas so we can render
// section titles and field labels for structured payloads.
const MI_SECTION_MAP = Object.fromEntries(MASTER_INTAKE_SCHEMA.map(s => [s.key, s]));
const MI_FIELD_LABEL = (() => {
  const m = {};
  for (const s of MASTER_INTAKE_SCHEMA) {
    for (const f of (s.fields || [])) m[`${s.key}.${f.key}`] = f.label;
  }
  return m;
})();
const CF_SECTION_MAP = Object.fromEntries(COUNSELOR_FOLLOWUP_SCHEMA.map(s => [s.key, s]));
const CF_FIELD_LABEL = (() => {
  const m = {};
  for (const s of COUNSELOR_FOLLOWUP_SCHEMA) {
    for (const f of (s.fields || [])) m[`${s.key}.${f.key}`] = f.label;
  }
  return m;
})();

const REVIEW_META = {
  pending:       { label: 'Pending review', bg: 'bg-amber-500/15',  fg: 'text-amber-300',  icon: Hourglass },
  reviewed:      { label: 'Reviewed',       bg: 'bg-emerald-500/15', fg: 'text-emerald-300', icon: CheckCircle },
  needs_action:  { label: 'Needs action',   bg: 'bg-rose-500/15',    fg: 'text-rose-300',    icon: AlertCircle },
};

const BUCKET_META = {
  subscriber_intakes: {
    label: 'Subscriber Intake', accent: '#D4A843', icon: User,
    sourceBadge: 'Subscriber', sourceBg: 'bg-amber-500/15', sourceFg: 'text-amber-300',
    sectionMap: MI_SECTION_MAP, fieldLabels: MI_FIELD_LABEL,
  },
  counselor_followups: {
    label: 'Counselor Follow-Up', accent: '#7E9EC8', icon: HeartPulse,
    sourceBadge: 'Counselor', sourceBg: 'bg-sky-500/15', sourceFg: 'text-sky-300',
    sectionMap: CF_SECTION_MAP, fieldLabels: CF_FIELD_LABEL,
  },
  counselor_observations: {
    label: 'Counselor Observation', accent: '#06b6d4', icon: FileText,
    sourceBadge: 'Counselor', sourceBg: 'bg-cyan-500/15', sourceFg: 'text-cyan-300',
    sectionMap: {}, fieldLabels: {},
  },
};

function formatValue(v) {
  if (v === null || v === undefined || v === '') return null;
  if (Array.isArray(v)) return v.length ? v.join(', ') : null;
  if (typeof v === 'object') {
    if (v.name) return v.name; // file-like
    const s = JSON.stringify(v);
    return s.length > 120 ? s.slice(0, 117) + '…' : s;
  }
  return String(v);
}

function buildSummaryPreview(bucket, entry) {
  // Intakes have entry.snapshot.answers (section → fieldKey → value).
  // Follow-ups have entry.structured_notes (section → fieldKey → value) OR entry.content.
  const out = [];
  if (bucket === 'subscriber_intakes') {
    const answers = (entry.snapshot && entry.snapshot.answers) || {};
    const priority = ['basics', 'bas', 'nas', 'tas', 'pattern_trigger', 'goals'];
    for (const sk of priority) {
      const sec = MI_SECTION_MAP[sk];
      const data = answers[sk];
      if (!sec || !data) continue;
      for (const f of (sec.fields || [])) {
        const v = formatValue(data[f.key]);
        if (v) { out.push({ label: f.label, value: v }); break; }
      }
      if (out.length >= 3) break;
    }
  } else if (bucket === 'counselor_followups') {
    const notes = entry.structured_notes || {};
    const priority = ['counselor_summary', 'identity_condition', 'symptom_intelligence', 'biological_terrain'];
    for (const sk of priority) {
      const sec = CF_SECTION_MAP[sk];
      const data = notes[sk];
      if (!sec || !data) continue;
      for (const f of (sec.fields || [])) {
        const v = formatValue(data[f.key]);
        if (v) { out.push({ label: f.label, value: v }); break; }
      }
      if (out.length >= 3) break;
    }
    if (out.length === 0 && entry.content) {
      out.push({ label: 'Note', value: String(entry.content).slice(0, 160) });
    }
  } else if (entry.content) {
    out.push({ label: 'Observation', value: String(entry.content).slice(0, 160) });
  }
  return out;
}

function StructuredDetail({ bucket, entry }) {
  const meta = BUCKET_META[bucket];
  const source = bucket === 'subscriber_intakes'
    ? ((entry.snapshot && entry.snapshot.answers) || {})
    : (entry.structured_notes || {});

  const sectionKeys = Object.keys(source).filter(k => {
    const v = source[k];
    return v && (typeof v !== 'object' || Object.keys(v).length > 0);
  });

  if (sectionKeys.length === 0) {
    if (entry.content) {
      return (
        <div className="rounded-md bg-black/30 border border-white/[0.05] p-3 text-[11px] text-white/70 whitespace-pre-wrap">
          {entry.content}
        </div>
      );
    }
    return <p className="text-[11px] text-white/30 italic">No structured data in this entry.</p>;
  }

  return (
    <div className="space-y-3" data-testid="queue-entry-structured">
      {sectionKeys.map(sk => {
        const sec = meta.sectionMap[sk];
        const sectionTitle = sec?.title || sk.replace(/_/g, ' ');
        const data = source[sk] || {};
        const rows = Object.entries(data)
          .map(([fk, raw]) => ({
            key: fk,
            label: meta.fieldLabels[`${sk}.${fk}`] || fk.replace(/_/g, ' '),
            value: formatValue(raw),
          }))
          .filter(r => r.value !== null);
        if (rows.length === 0) return null;
        return (
          <div key={sk} className="rounded-md border border-white/[0.06] bg-white/[0.015] p-3">
            <div className="text-[10.5px] uppercase tracking-wider text-white/50 font-semibold mb-2">
              {sectionTitle}
            </div>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5">
              {rows.map(r => (
                <div key={r.key} className="flex flex-col">
                  <dt className="text-[10px] text-white/40 leading-tight">{r.label}</dt>
                  <dd className="text-[11.5px] text-white/85 leading-snug break-words">{r.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        );
      })}
      {bucket === 'counselor_followups' && entry.content ? (
        <div className="rounded-md border border-white/[0.06] bg-white/[0.015] p-3">
          <div className="text-[10.5px] uppercase tracking-wider text-white/50 font-semibold mb-1">
            Summary line
          </div>
          <p className="text-[11.5px] text-white/85 whitespace-pre-wrap">{entry.content}</p>
        </div>
      ) : null}
    </div>
  );
}

function ReviewActions({ subscriberId, bucket, entry, onReload }) {
  const [busy, setBusy] = useState(null);
  const [showNotes, setShowNotes] = useState(false);
  const [notes, setNotes] = useState(entry.review_notes || '');
  const [demoteModal, setDemoteModal] = useState(null); // { targetStatus, reason }
  const current = entry.review_status || 'pending';

  const patchReviewStatus = async (status, reason) => {
    try {
      setBusy(status);
      await axios.patch(
        `${API}/biocontext-memory/${subscriberId}/input-queue/${bucket}/${entry.id}/review`,
        {
          review_status: status,
          review_notes: reason !== undefined ? reason : (notes || ''),
        },
      );
      toast.success(status === 'reviewed' ? 'Marked as reviewed' :
                    status === 'needs_action' ? 'Flagged — needs action' :
                    'Reset to pending');
      onReload?.();
      setDemoteModal(null);
    } catch (e) {
      console.error(e);
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.error === 'review_demotion_reason_required') {
        toast.error('Reason is required to demote a reviewed entry.');
        setDemoteModal({ targetStatus: status, reason: '' });
      } else {
        toast.error((typeof detail === 'string' ? detail : detail?.message) || 'Could not update review status');
      }
    } finally {
      setBusy(null);
    }
  };

  const handleClick = (status) => {
    // Demoting a reviewed entry → prompt for reason first
    const isDemotion = current === 'reviewed' && (status === 'pending' || status === 'needs_action');
    if (isDemotion) {
      setDemoteModal({ targetStatus: status, reason: '' });
      return;
    }
    patchReviewStatus(status);
  };

  const submitDemotion = () => {
    const reason = (demoteModal?.reason || '').trim();
    if (!reason) {
      toast.error('Reason is required.');
      return;
    }
    patchReviewStatus(demoteModal.targetStatus, reason);
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`queue-entry-actions-${entry.id}`}>
      <Button
        size="sm" variant="outline"
        className="h-7 px-2 text-[10.5px] border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
        onClick={() => handleClick('reviewed')}
        disabled={busy !== null || current === 'reviewed'}
        data-testid={`queue-entry-mark-reviewed-${entry.id}`}
      >
        {busy === 'reviewed' ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3 mr-1" />}
        {current === 'reviewed' ? 'Reviewed' : 'Mark reviewed'}
      </Button>
      <Button
        size="sm" variant="outline"
        className="h-7 px-2 text-[10.5px] border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
        onClick={() => handleClick('needs_action')}
        disabled={busy !== null || current === 'needs_action'}
        data-testid={`queue-entry-needs-action-${entry.id}`}
      >
        {busy === 'needs_action' ? <Loader2 className="w-3 h-3 animate-spin" /> : <AlertCircle className="w-3 h-3 mr-1" />}
        Needs action
      </Button>
      {current !== 'pending' ? (
        <Button
          size="sm" variant="ghost"
          className="h-7 px-2 text-[10.5px] text-white/50 hover:text-white/80"
          onClick={() => handleClick('pending')}
          disabled={busy !== null}
          data-testid={`queue-entry-reset-${entry.id}`}
        >
          Reset
        </Button>
      ) : null}
      <button
        type="button"
        onClick={() => setShowNotes(v => !v)}
        className="text-[10.5px] text-white/40 hover:text-white/70 underline"
        data-testid={`queue-entry-notes-toggle-${entry.id}`}
      >
        {showNotes ? 'Hide notes' : (entry.review_notes ? 'Edit notes' : 'Add notes')}
      </button>
      {showNotes ? (
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="mt-2 w-full bg-black/30 border-white/[0.08] text-[11.5px] text-white/85"
          placeholder="Short note for the care team (optional)"
          data-testid={`queue-entry-notes-${entry.id}`}
        />
      ) : null}

      {/* Demotion-reason modal */}
      {demoteModal && (
        <div
          data-testid={`queue-entry-demote-modal-${entry.id}`}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setDemoteModal(null); }}
        >
          <div className="w-full max-w-md rounded-lg border border-amber-500/30 bg-[#10141d] p-5">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-amber-300" />
              <h3 className="text-sm text-white/90">Demote reviewed entry</h3>
            </div>
            <p className="text-[11px] text-white/50 mb-4">
              This entry is currently <span className="text-emerald-300">reviewed</span>.
              Moving it back to <span className="text-amber-300">{demoteModal.targetStatus}</span> requires a reason
              and will be written to the change history.
            </p>
            <Textarea
              value={demoteModal.reason}
              onChange={(e) => setDemoteModal({ ...demoteModal, reason: e.target.value })}
              rows={4}
              placeholder="Reason for demoting (e.g. new follow-up changed the answer, additional context arrived)…"
              className="w-full bg-black/40 border-white/10 text-white/90 text-xs"
              data-testid={`queue-entry-demote-reason-${entry.id}`}
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button
                size="sm" variant="outline"
                className="h-7 px-3 text-[10px] border-white/10 text-white/70"
                onClick={() => setDemoteModal(null)}
                data-testid={`queue-entry-demote-cancel-${entry.id}`}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="h-7 px-3 text-[10px] bg-amber-700/40 text-amber-100 hover:bg-amber-700/60"
                onClick={submitDemotion}
                disabled={busy !== null}
                data-testid={`queue-entry-demote-submit-${entry.id}`}
              >
                Demote with reason
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function QueueEntry({ bucket, entry, subscriberId, onReload }) {
  const meta = BUCKET_META[bucket];
  const Icon = meta.icon;
  const reviewStatus = entry.review_status || 'pending';
  const RM = REVIEW_META[reviewStatus] || REVIEW_META.pending;
  const RMIcon = RM.icon;
  const preview = buildSummaryPreview(bucket, entry);
  const [expanded, setExpanded] = useState(false);

  // Caregiver-submitted detection (flag may live on entry or snapshot)
  const snap = entry.snapshot || {};
  const isCaregiverSubmitted =
    entry.caregiver_submitted === true ||
    snap.caregiver_submitted === true ||
    entry.submitted_by_role === 'caregiver' ||
    snap.submitted_by_role === 'caregiver';
  const caregiverName = snap.submitted_by_name || entry.submitted_by_name || null;

  const reviewedLine = entry.reviewed_at
    ? `${entry.reviewed_by_name || 'Admin'} · ${new Date(entry.reviewed_at).toLocaleString()}`
    : null;

  return (
    <div
      className="rounded-lg border border-white/[0.06] bg-white/[0.02] overflow-hidden"
      data-testid={`queue-entry-${entry.id}`}
    >
      {/* Header strip */}
      <div className="flex items-start gap-3 p-3">
        <div
          className="h-7 w-7 rounded-md flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${meta.accent}22`, color: meta.accent }}
        >
          <Icon className="w-3.5 h-3.5" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`text-[9.5px] px-1.5 py-0.5 rounded-full ${meta.sourceBg} ${meta.sourceFg} font-medium uppercase tracking-wider`}>
              {meta.sourceBadge}
            </span>
            <span className="text-[10.5px] text-white/80 font-medium">{meta.label}</span>
            <span className={`inline-flex items-center gap-1 text-[9.5px] px-1.5 py-0.5 rounded-full ${RM.bg} ${RM.fg} font-medium`}
                  data-testid={`queue-entry-review-status-${entry.id}`}>
              <RMIcon className="w-3 h-3" /> {RM.label}
            </span>
            {isCaregiverSubmitted ? (
              <span
                className="inline-flex items-center gap-1 text-[9.5px] px-1.5 py-0.5 rounded-full bg-violet-500/15 text-violet-300 font-medium"
                data-testid={`queue-entry-caregiver-badge-${entry.id}`}
                title={caregiverName ? `Submitted by caregiver: ${caregiverName}` : 'Submitted by caregiver'}
              >
                <UserRound className="w-3 h-3" />
                {caregiverName
                  ? <>Submitted by caregiver:&nbsp;<span className="text-violet-200/85 font-normal">{caregiverName}</span></>
                  : 'Submitted by caregiver'}
              </span>
            ) : null}
          </div>

          <div className="text-[10.5px] text-white/50 flex items-center gap-2 flex-wrap">
            <span>
              <span className="text-white/40">submitted by</span>{' '}
              <span className="text-white/75 font-medium">{entry.by_name || entry.by || 'System'}</span>
            </span>
            <span className="text-white/20">·</span>
            <span>{entry.at ? new Date(entry.at).toLocaleString() : ''}</span>
            {entry.source ? (
              <>
                <span className="text-white/20">·</span>
                <span className="text-white/40">via {entry.source}</span>
              </>
            ) : null}
          </div>

          {/* Summary preview */}
          {preview.length > 0 ? (
            <dl className="mt-2 space-y-0.5">
              {preview.map((p, i) => (
                <div key={i} className="flex gap-2 text-[11px]">
                  <dt className="text-white/40 flex-shrink-0">{p.label}:</dt>
                  <dd className="text-white/80 truncate">{p.value}</dd>
                </div>
              ))}
            </dl>
          ) : null}

          {/* Tags */}
          {entry.tags && entry.tags.length > 0 ? (
            <div className="flex flex-wrap gap-1 mt-2">
              {entry.tags.map((t, i) => (
                <span key={i} className="text-[9.5px] text-white/40 px-1.5 py-0.5 rounded-full bg-white/[0.04]">
                  {t}
                </span>
              ))}
            </div>
          ) : null}

          {reviewedLine ? (
            <div className="mt-2 text-[10px] text-white/35">
              Reviewed by {reviewedLine}
              {entry.review_notes ? ` — “${entry.review_notes}”` : ''}
            </div>
          ) : null}
        </div>

        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="flex items-center gap-1 text-[10.5px] text-white/50 hover:text-white/80 flex-shrink-0 self-start"
          data-testid={`queue-entry-toggle-${entry.id}`}
        >
          {expanded ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          {expanded ? 'Hide' : 'View'}
          {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        </button>
      </div>

      {/* Expanded body */}
      {expanded ? (
        <div className="border-t border-white/[0.06] bg-black/20 p-3 space-y-3">
          <StructuredDetail bucket={bucket} entry={entry} />
          <div className="pt-2 border-t border-white/[0.04]">
            <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">Review</div>
            <ReviewActions
              subscriberId={subscriberId}
              bucket={bucket}
              entry={entry}
              onReload={onReload}
            />
            <p className="text-[10px] text-white/30 mt-2">
              Review status is non-destructive. Admin still owns canonical writes through the Canonical tab —
              nothing here is promoted automatically.
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function QueueBucket({ title, bucket, entries, subscriberId, onReload, filter, onFilterChange }) {
  const counts = useMemo(() => {
    const c = { all: entries.length, pending: 0, reviewed: 0, needs_action: 0 };
    for (const e of entries) {
      const s = e.review_status || 'pending';
      if (c[s] !== undefined) c[s] += 1;
    }
    return c;
  }, [entries]);

  const filtered = filter === 'all' ? entries : entries.filter(e => (e.review_status || 'pending') === filter);

  const chip = (key, label) => (
    <button
      type="button"
      onClick={() => onFilterChange(key)}
      data-testid={`queue-filter-${bucket}-${key}`}
      className={[
        'text-[10px] px-2 py-0.5 rounded-full border transition-colors',
        filter === key
          ? 'border-white/30 bg-white/10 text-white'
          : 'border-white/10 bg-transparent text-white/50 hover:text-white/80',
      ].join(' ')}
    >{label} <span className="text-white/40">({counts[key]})</span></button>
  );

  return (
    <Section title={`${title} (${entries.length})`}>
      <div className="flex items-center gap-1.5 flex-wrap mb-3">
        {chip('all', 'All')}
        {chip('pending', 'Pending')}
        {chip('needs_action', 'Needs action')}
        {chip('reviewed', 'Reviewed')}
      </div>
      <div className="space-y-2">
        {filtered.length === 0 ? (
          <p className="text-[11px] text-white/30 italic px-1 py-2">No entries match this filter.</p>
        ) : filtered.map(entry => (
          <QueueEntry
            key={entry.id}
            bucket={bucket}
            entry={entry}
            subscriberId={subscriberId}
            onReload={onReload}
          />
        ))}
      </div>
    </Section>
  );
}

function InputQueueTab({ data, subscriberId, onReload }) {
  const [urlParams] = useSearchParams();
  const urlFilter = urlParams.get('filter');
  const initial = (urlFilter && ['all', 'pending', 'reviewed', 'needs_action'].includes(urlFilter))
    ? urlFilter : 'all';

  const iq = data.input_queue || {};
  const subs = iq.subscriber_intakes || [];
  const followups = iq.counselor_followups || [];
  const obs = iq.counselor_observations || [];
  const empty = subs.length === 0 && followups.length === 0 && obs.length === 0;

  const [filters, setFilters] = useState({
    subscriber_intakes: initial,
    counselor_followups: initial,
    counselor_observations: initial,
  });
  const setFilter = (bucket, v) => setFilters(prev => ({ ...prev, [bucket]: v }));

  const pendingTotal =
    subs.filter(e => (e.review_status || 'pending') === 'pending').length
    + followups.filter(e => (e.review_status || 'pending') === 'pending').length
    + obs.filter(e => (e.review_status || 'pending') === 'pending').length;

  return (
    <div className="space-y-4" data-testid="input-queue-tab">
      {!empty ? (
        <div className="rounded-md border border-white/[0.06] bg-white/[0.02] px-3 py-2 flex items-center gap-2 flex-wrap"
             data-testid="input-queue-summary">
          <Inbox className="w-3.5 h-3.5 text-white/50" />
          <span className="text-[11px] text-white/70">
            {subs.length + followups.length + obs.length} entries
          </span>
          <span className="text-white/20">·</span>
          <span className="text-[11px] text-amber-300">{pendingTotal} pending review</span>
          <span className="ml-auto text-[10px] text-white/40">
            Reviewing here does not change canonical context.
          </span>
        </div>
      ) : null}

      {empty && (
        <p className="text-xs text-white/30 italic text-center py-8">
          No inputs yet. Subscriber intakes and counselor inputs will appear here as source material.
        </p>
      )}

      {subs.length > 0 && (
        <QueueBucket
          title="Subscriber Intakes" bucket="subscriber_intakes"
          entries={subs} subscriberId={subscriberId} onReload={onReload}
          filter={filters.subscriber_intakes}
          onFilterChange={(v) => setFilter('subscriber_intakes', v)}
        />
      )}
      {followups.length > 0 && (
        <QueueBucket
          title="Counselor Follow-ups" bucket="counselor_followups"
          entries={followups} subscriberId={subscriberId} onReload={onReload}
          filter={filters.counselor_followups}
          onFilterChange={(v) => setFilter('counselor_followups', v)}
        />
      )}
      {obs.length > 0 && (
        <QueueBucket
          title="Counselor Observations" bucket="counselor_observations"
          entries={obs} subscriberId={subscriberId} onReload={onReload}
          filter={filters.counselor_observations}
          onFilterChange={(v) => setFilter('counselor_observations', v)}
        />
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
//  Tab 3 — System Suggestions
// ═════════════════════════════════════════════════════════════════════
function SuggestionsTab({ data, onReview }) {
  const suggestions = data.system_suggestions || [];
  const pending = suggestions.filter(s => s.status === 'suggested');
  const reviewed = suggestions.filter(s => s.status !== 'suggested');

  return (
    <div className="space-y-4" data-testid="suggestions-tab">
      {pending.length === 0 && reviewed.length === 0 && (
        <p className="text-xs text-white/30 italic text-center py-8">
          No system suggestions yet. As labs, execution, TwinFlō, passive signals, support, and reports
          generate evidence, suggestions will appear here for your review.
        </p>
      )}
      {pending.length > 0 && (
        <Section title={`Pending Review (${pending.length})`}>
          <div className="space-y-2">
            {pending.map(s => (
              <SuggestionRow key={s.id} s={s} subscriberId={data.subscriber_id} onReview={onReview} />
            ))}
          </div>
        </Section>
      )}
      {reviewed.length > 0 && (
        <Section title={`Reviewed (${reviewed.length})`}>
          <div className="space-y-2">
            {reviewed.map(s => (
              <SuggestionRow key={s.id} s={s} subscriberId={data.subscriber_id} onReview={onReview} />
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

function SuggestionRow({ s, subscriberId, onReview }) {
  const Icon = SOURCE_ICON[s.source_type] || SOURCE_ICON.unknown;
  const [busy, setBusy] = useState(false);

  const review = async (status) => {
    setBusy(true);
    try {
      await axios.patch(
        `${API}/biocontext-memory/${subscriberId}/system-suggestions/${s.id}`,
        { status },
      );
      toast.success(`Suggestion ${status}`);
      onReview?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally { setBusy(false); }
  };

  const isPending = s.status === 'suggested';

  return (
    <div className={`rounded-lg p-3 border ${
      s.status === 'accepted' ? 'border-emerald-700/30 bg-emerald-500/[0.03]' :
      s.status === 'dismissed' ? 'border-white/[0.04] bg-white/[0.01] opacity-60' :
      'border-white/[0.08] bg-white/[0.02]'
    }`} data-testid={`suggestion-${s.id}`}>
      <div className="flex items-start gap-3">
        <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: s.source_type === 'lab' ? '#7E9EC8' : '#a78bfa' }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Badge variant="outline" className="border-white/10 text-white/60 text-[9.5px] capitalize">{s.source_type}</Badge>
            <Badge variant="outline" className="text-[9.5px]" style={{ borderColor: `${CONF_COLOR[s.confidence]}40`, color: CONF_COLOR[s.confidence] }}>
              {s.confidence}
            </Badge>
            {s.status !== 'suggested' && (
              <Badge variant="outline" className={`text-[9.5px] ${
                s.status === 'accepted' ? 'border-emerald-700/40 text-emerald-400' : 'border-white/10 text-white/40'
              }`}>
                {s.status}
              </Badge>
            )}
          </div>
          <p className="text-xs text-white/80 leading-relaxed font-['Work_Sans']">{s.message}</p>
          {(s.affected_sections || []).length > 0 && (
            <p className="text-[10px] text-white/40 mt-1 font-['Work_Sans']">
              Affects: {s.affected_sections.join(', ')}
            </p>
          )}
          <p className="text-[9.5px] text-white/25 mt-1">
            {s.created_at ? new Date(s.created_at).toLocaleString() : ''}
            {s.reviewed_at && <> · reviewed {new Date(s.reviewed_at).toLocaleString()}</>}
          </p>
        </div>
        {isPending && (
          <div className="flex flex-col gap-1 flex-shrink-0">
            <Button size="sm" variant="outline" className="h-7 px-2.5 text-[10px] border-emerald-700/30 text-emerald-400 hover:bg-emerald-900/20"
                    disabled={busy} onClick={() => review('accepted')} data-testid={`accept-${s.id}`}>
              <CheckCircle2 className="w-3 h-3 mr-1" /> Accept
            </Button>
            <Button size="sm" variant="outline" className="h-7 px-2.5 text-[10px] border-white/10 text-white/50 hover:text-white/80"
                    disabled={busy} onClick={() => review('dismissed')} data-testid={`dismiss-${s.id}`}>
              <XCircle className="w-3 h-3 mr-1" /> Dismiss
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
//  Tab 4 — Active Case Reading
// ═════════════════════════════════════════════════════════════════════
function ActiveCaseTab({ data, onUpdated }) {
  const acr = data.active_case_reading || {};
  const [form, setForm] = useState({
    current_dominant_driver: acr.current_dominant_driver || '',
    current_amplifier: acr.current_amplifier || '',
    current_contradiction: acr.current_contradiction || '',
    current_life_blocker: acr.current_life_blocker || '',
    current_support_advantage: acr.current_support_advantage || '',
    sections_needing_recheck: (acr.sections_needing_recheck || []).join(', '),
    notes_for_next_review: acr.notes_for_next_review || '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      current_dominant_driver: acr.current_dominant_driver || '',
      current_amplifier: acr.current_amplifier || '',
      current_contradiction: acr.current_contradiction || '',
      current_life_blocker: acr.current_life_blocker || '',
      current_support_advantage: acr.current_support_advantage || '',
      sections_needing_recheck: (acr.sections_needing_recheck || []).join(', '),
      notes_for_next_review: acr.notes_for_next_review || '',
    });
  }, [acr]); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/biocontext-memory/${data.subscriber_id}/active-case-reading`, {
        ...form,
        sections_needing_recheck: form.sections_needing_recheck
          .split(',').map(s => s.trim()).filter(Boolean),
      });
      toast.success('Active case reading saved');
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const Field = ({ name, label, placeholder, multi = false, testid }) => (
    <div>
      <label className="text-[10px] uppercase tracking-[0.15em] text-white/40 mb-1 block font-['Work_Sans']">
        {label}
      </label>
      {multi ? (
        <Textarea
          value={form[name]}
          onChange={e => setForm({ ...form, [name]: e.target.value })}
          placeholder={placeholder}
          rows={2}
          className="bg-white/[0.02] border-white/[0.08] text-white/80 text-xs font-['Work_Sans']"
          data-testid={testid}
        />
      ) : (
        <Input
          value={form[name]}
          onChange={e => setForm({ ...form, [name]: e.target.value })}
          placeholder={placeholder}
          className="bg-white/[0.02] border-white/[0.08] text-white/80 h-8 text-xs"
          data-testid={testid}
        />
      )}
    </div>
  );

  return (
    <div className="rounded-xl p-5 border border-white/[0.06] bg-[#161B22] space-y-4" data-testid="active-case-tab">
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-white/35 font-['Work_Sans']">
          What matters right now
        </p>
        <p className="text-[10.5px] text-white/30 mt-1 font-['Work_Sans']">
          This is the dynamic reading of the case. Read by backbone, DECODE, Care Ops, and Support.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field name="current_dominant_driver" label="Current dominant driver"
               placeholder="e.g. methylation strain" testid="field-driver" />
        <Field name="current_amplifier" label="Current amplifier"
               placeholder="e.g. low reserve terrain" testid="field-amplifier" />
        <Field name="current_contradiction" label="Current contradiction"
               placeholder="e.g. lab-normal but functionally stressed" testid="field-contradiction" />
        <Field name="current_life_blocker" label="Current life blocker"
               placeholder="e.g. poor sleep environment, caregiver overload" testid="field-blocker" />
        <Field name="current_support_advantage" label="Current support advantage"
               placeholder="e.g. engaged partner, strong meal structure" testid="field-advantage" />
        <Field name="sections_needing_recheck" label="Sections needing recheck"
               placeholder="comma-separated: biological_terrain, medication_response" testid="field-recheck" />
      </div>
      <Field name="notes_for_next_review" label="Notes for next review" multi
             placeholder="What to look for next time — patterns emerging, questions to ask, things to verify." testid="field-notes" />
      <div className="flex justify-end">
        <Button size="sm" className="h-8 text-[10.5px] bg-[#D4A843] text-black hover:bg-[#D4A843]/80"
                onClick={save} disabled={saving} data-testid="save-active-case">
          {saving ? 'Saving…' : 'Save active case reading'}
        </Button>
      </div>
      {acr.updated_at && (
        <p className="text-[10px] text-white/25 text-right font-['Work_Sans']">
          Last updated {new Date(acr.updated_at).toLocaleString()}
        </p>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
//  Tab 5 — History
// ═════════════════════════════════════════════════════════════════════
const ACTION_LABEL = {
  'canonical.update': 'Canonical section updated',
  'system.suggestion.added': 'System suggestion created',
  'system.suggestion.accepted': 'System suggestion accepted',
  'system.suggestion.dismissed': 'System suggestion dismissed',
  'input.subscriber_intake': 'Subscriber intake added',
  'input.counselor_followup': 'Counselor follow-up added',
  'input.counselor_observation': 'Counselor observation added',
  'counselor.submit.followup': 'Counselor submitted follow-up',
  'counselor.submit.observation': 'Counselor submitted observation',
  'active_case_reading.update': 'Active case reading updated',
};

function HistoryTab({ history }) {
  if (!history || history.length === 0) {
    return <p className="text-xs text-white/30 italic text-center py-8" data-testid="history-empty">No history entries yet.</p>;
  }
  return (
    <div className="space-y-2" data-testid="history-tab">
      {history.map(h => (
        <div key={h.id} className="rounded-lg p-3 border border-white/[0.06] bg-white/[0.015]" data-testid={`history-${h.id}`}>
          <div className="flex items-center gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-[#7E9EC8] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-white/80 font-['Work_Sans']">
                {ACTION_LABEL[h.action] || h.action}
              </p>
              <p className="text-[10px] text-white/35 mt-0.5 font-['Work_Sans']">
                by {h.by_name || h.by || 'system'} ({h.by_role || '—'}) · {h.at ? new Date(h.at).toLocaleString() : ''}
              </p>
            </div>
          </div>
          {h.detail && Object.keys(h.detail).length > 0 && (
            <div className="mt-1.5 pl-5 text-[10px] text-white/40 font-mono">
              {JSON.stringify(h.detail)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
